import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertCircle, Loader2, PenTool, PhoneCall, ListChecks } from "lucide-react";
import { Reveal, SectionLabel } from "../components/Reveal";

// In produzione /api/contact è servita dalla Cloudflare Pages Function
// (frontend/functions/api/contact.js).
const CONTACT_ENDPOINT = "/api/contact";

const SETTORI = [
  "Ristorante",
  "Bar",
  "B&B / struttura ricettiva",
  "Lido / stabilimento balneare",
  "Barber shop / parrucchiere",
  "Centro estetico",
  "Palestra / fitness",
  "Professionista",
  "Negozio",
  "Impresa di servizi",
  "Altro",
];

const RUOLI = [
  "Titolare / proprietario",
  "Socio",
  "Responsabile",
  "Marketing / Social",
  "Collaboratore",
  "Altro",
];

const INITIAL = {
  nome: "",
  attivita: "",
  settore: "",
  ruolo: "",
  email: "",
  telefono: "",
  ricontatto: "",
  messaggio: "",
  privacy: false,
  website: "", // honeypot antispam
};

const inputClass =
  "w-full rounded-lg border border-line bg-white px-4 py-3 text-base text-navy-dark placeholder:text-steel/50 transition-colors duration-200 focus:border-celeste focus:outline-none focus:ring-2 focus:ring-celeste/30";
const labelClass = "mb-1.5 block text-sm font-semibold text-navy-dark";
const errorClass = "mt-1.5 text-sm text-red-700";

const Field = ({ id, label, required = true, error, children }) => (
  <div>
    <label htmlFor={id} className={labelClass}>
      {label} {required && <span aria-hidden="true" className="text-celeste-dark">*</span>}
    </label>
    {children}
    {error && (
      <p id={`${id}-error`} role="alert" className={errorClass}>
        {error}
      </p>
    )}
  </div>
);

const ASSURANCES = [
  { icon: PenTool, text: "Prima bozza gratuita prima di decidere" },
  { icon: PhoneCall, text: "Ricontatto via email o telefonata, come preferisci" },
  { icon: ListChecks, text: "Proposta chiara con caratteristiche e costo prima dell'inizio" },
];

export const Contact = () => {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [serverMessage, setServerMessage] = useState("");

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((err) => ({ ...err, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (form.nome.trim().length < 2) next.nome = "Inserisci nome e cognome.";
    if (form.attivita.trim().length < 2) next.attivita = "Inserisci il nome dell'attività.";
    if (!form.settore) next.settore = "Seleziona il settore dell'attività.";
    if (!form.ruolo) next.ruolo = "Seleziona il tuo ruolo.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) next.email = "Inserisci un indirizzo email valido.";
    if (form.telefono.replace(/\D/g, "").length < 6) next.telefono = "Inserisci un numero di telefono valido.";
    if (!form.ricontatto) next.ricontatto = "Scegli come preferisci essere ricontattato.";
    if (form.messaggio.trim().length < 10) next.messaggio = "Raccontaci brevemente la tua richiesta (almeno 10 caratteri).";
    if (!form.privacy) next.privacy = "È necessario accettare l'informativa privacy per inviare la richiesta.";
    return next;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) {
      const first = document.querySelector("[aria-invalid='true']");
      first?.focus();
      return;
    }
    setStatus("sending");
    setServerMessage("");
    try {
      await axios.post(CONTACT_ENDPOINT, { ...form, nome: form.nome.trim(), attivita: form.attivita.trim(), messaggio: form.messaggio.trim() });
      setStatus("success");
    } catch (err) {
      const detail = err?.response?.data;
      const code = detail?.error || detail?.detail;
      if (code === "email_not_configured") {
        setServerMessage(
          "Il servizio di invio email è in fase di configurazione: al momento la richiesta non può essere inviata. Ti invitiamo a riprovare a breve."
        );
      } else if (code === "rate_limited" || err?.response?.status === 429) {
        setServerMessage("Hai inviato troppe richieste in poco tempo. Riprova tra qualche minuto.");
      } else if (code === "invalid_input" || err?.response?.status === 422) {
        setServerMessage("Alcuni campi non risultano validi. Controlla i dati inseriti e riprova.");
      } else {
        setServerMessage("Non è stato possibile inviare la richiesta. Riprova più tardi.");
      }
      setStatus("error");
    }
  };

  return (
    <section id="contatti" aria-labelledby="contatti-title" className="border-t border-line bg-white">
      <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-28">
        <div className="grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <SectionLabel>Contatti</SectionLabel>
            <h2 id="contatti-title" className="mt-4 font-display text-3xl font-bold tracking-tight text-navy-dark sm:text-4xl">
              Parlaci del tuo progetto.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-steel sm:text-lg">
              Raccontaci brevemente la tua attività e cosa vorresti realizzare. Valuteremo la
              richiesta e ti ricontatteremo per capire insieme la soluzione più adatta.
            </p>
            <ul className="mt-9 space-y-4">
              {ASSURANCES.map((item) => (
                <li key={item.text} className="flex items-center gap-3.5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-celeste-soft text-celeste-dark" aria-hidden="true">
                    <item.icon className="h-5 w-5" />
                  </span>
                  <span className="text-sm font-medium text-navy-dark sm:text-base">{item.text}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={0.1}>
            {status === "success" ? (
              <div
                role="status"
                data-testid="contact-success-message"
                className="flex h-full flex-col items-start justify-center rounded-2xl border border-line bg-mist p-8 sm:p-12"
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-celeste-soft text-celeste-dark" aria-hidden="true">
                  <CheckCircle2 className="h-7 w-7" />
                </span>
                <h3 className="mt-6 font-display text-2xl font-bold text-navy-dark">Richiesta inviata correttamente.</h3>
                <p className="mt-3 max-w-md text-base leading-relaxed text-steel">
                  Grazie per averci contattato. Esamineremo il tuo progetto e ti ricontatteremo
                  utilizzando i recapiti indicati.
                </p>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                noValidate
                data-testid="contact-form"
                className="rounded-2xl border border-line bg-white p-6 shadow-[0_20px_60px_-30px_rgba(11,31,51,0.2)] sm:p-9"
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field id="contact-nome" label="Nome e cognome" error={errors.nome}>
                    <input
                      id="contact-nome"
                      type="text"
                      autoComplete="name"
                      value={form.nome}
                      onChange={set("nome")}
                      aria-invalid={!!errors.nome}
                      aria-describedby={errors.nome ? "contact-nome-error" : undefined}
                      data-testid="contact-input-name"
                      className={inputClass}
                    />
                  </Field>
                  <Field id="contact-attivita" label="Nome attività" error={errors.attivita}>
                    <input
                      id="contact-attivita"
                      type="text"
                      autoComplete="organization"
                      value={form.attivita}
                      onChange={set("attivita")}
                      aria-invalid={!!errors.attivita}
                      aria-describedby={errors.attivita ? "contact-attivita-error" : undefined}
                      data-testid="contact-input-company"
                      className={inputClass}
                    />
                  </Field>
                  <Field id="contact-settore" label="Settore dell'attività" error={errors.settore}>
                    <select
                      id="contact-settore"
                      value={form.settore}
                      onChange={set("settore")}
                      aria-invalid={!!errors.settore}
                      aria-describedby={errors.settore ? "contact-settore-error" : undefined}
                      data-testid="contact-select-sector"
                      className={`${inputClass} ${form.settore ? "" : "text-steel/50"}`}
                    >
                      <option value="" disabled>Seleziona il settore</option>
                      {SETTORI.map((s) => (
                        <option key={s} value={s} className="text-navy-dark">{s}</option>
                      ))}
                    </select>
                  </Field>
                  <Field id="contact-ruolo" label="Ruolo all'interno dell'attività" error={errors.ruolo}>
                    <select
                      id="contact-ruolo"
                      value={form.ruolo}
                      onChange={set("ruolo")}
                      aria-invalid={!!errors.ruolo}
                      aria-describedby={errors.ruolo ? "contact-ruolo-error" : undefined}
                      data-testid="contact-select-role"
                      className={`${inputClass} ${form.ruolo ? "" : "text-steel/50"}`}
                    >
                      <option value="" disabled>Seleziona il ruolo</option>
                      {RUOLI.map((r) => (
                        <option key={r} value={r} className="text-navy-dark">{r}</option>
                      ))}
                    </select>
                  </Field>
                  <Field id="contact-email" label="Email" error={errors.email}>
                    <input
                      id="contact-email"
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={set("email")}
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? "contact-email-error" : undefined}
                      data-testid="contact-input-email"
                      className={inputClass}
                    />
                  </Field>
                  <Field id="contact-telefono" label="Telefono" error={errors.telefono}>
                    <input
                      id="contact-telefono"
                      type="tel"
                      autoComplete="tel"
                      value={form.telefono}
                      onChange={set("telefono")}
                      aria-invalid={!!errors.telefono}
                      aria-describedby={errors.telefono ? "contact-telefono-error" : undefined}
                      data-testid="contact-input-phone"
                      className={inputClass}
                    />
                  </Field>
                </div>

                <fieldset className="mt-5">
                  <legend className={labelClass}>Come preferisci essere ricontattato?</legend>
                  <div className="flex flex-wrap gap-3" role="radiogroup" data-testid="contact-radio-method">
                    {["Email", "Telefonata"].map((opt) => (
                      <label
                        key={opt}
                        className={`flex cursor-pointer items-center gap-2.5 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors duration-200 ${
                          form.ricontatto === opt
                            ? "border-celeste bg-celeste-soft text-navy-dark"
                            : "border-line text-steel hover:border-celeste/50"
                        }`}
                      >
                        <input
                          type="radio"
                          name="ricontatto"
                          value={opt}
                          checked={form.ricontatto === opt}
                          onChange={set("ricontatto")}
                          data-testid={`contact-radio-${opt.toLowerCase()}`}
                          className="h-4 w-4 accent-[#5DA9E9]"
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                  {errors.ricontatto && (
                    <p role="alert" className={errorClass}>{errors.ricontatto}</p>
                  )}
                </fieldset>

                <div className="mt-5">
                  <Field id="contact-messaggio" label="Raccontaci la tua richiesta" error={errors.messaggio}>
                    <textarea
                      id="contact-messaggio"
                      rows={5}
                      placeholder="Descrivi brevemente la tua attività, il sito che vorresti realizzare e le eventuali funzionalità di cui hai bisogno."
                      value={form.messaggio}
                      onChange={set("messaggio")}
                      aria-invalid={!!errors.messaggio}
                      aria-describedby={errors.messaggio ? "contact-messaggio-error" : undefined}
                      data-testid="contact-textarea-message"
                      className={`${inputClass} resize-y`}
                    />
                  </Field>
                </div>

                {/* Honeypot antispam: invisibile agli utenti reali */}
                <div className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
                  <label htmlFor="contact-website">Non compilare questo campo</label>
                  <input
                    id="contact-website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={set("website")}
                  />
                </div>

                <div className="mt-5">
                  <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-steel">
                    <input
                      type="checkbox"
                      checked={form.privacy}
                      onChange={set("privacy")}
                      aria-invalid={!!errors.privacy}
                      data-testid="contact-checkbox-privacy"
                      className="mt-0.5 h-5 w-5 shrink-0 accent-[#102A43]"
                    />
                    <span>
                      Ho letto l'{" "}
                      <Link
                        to="/privacy-policy"
                        data-testid="contact-privacy-link"
                        className="font-semibold text-navy underline decoration-celeste decoration-2 underline-offset-2 hover:text-celeste-dark"
                      >
                        informativa privacy
                      </Link>
                      . <span aria-hidden="true" className="text-celeste-dark">*</span>
                    </span>
                  </label>
                  {errors.privacy && (
                    <p role="alert" className={errorClass}>{errors.privacy}</p>
                  )}
                </div>

                {status === "error" && (
                  <div
                    role="alert"
                    data-testid="contact-error-message"
                    className="mt-5 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3.5 text-sm leading-relaxed text-red-800"
                  >
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    {serverMessage}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "sending"}
                  data-testid="contact-submit-button"
                  className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-navy px-6 py-4 text-base font-semibold text-white shadow-md transition-all duration-200 hover:bg-navy-dark hover:shadow-lg active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "sending" && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                  {status === "sending" ? "Invio in corso…" : "Invia la richiesta"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};
